import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    Button, Select, RangeSlider, Modal, Spinner,
} from 'flowbite-react';
import { Formik } from 'formik';
import { useRecoilState } from 'recoil';

import WarningCard from '@/components/commons/warning_card';
import { FETCH_BIO_BATCH, UPDATE_BRIEF, UPDATE_USER } from '@/components/onboarding/Actions';
import { BIO_NAMES, BIO_STATES, reportGeneralError } from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals';
import { CREATE_BIO_REFINEMENT, FETCH_BIO_REFINEMENT } from '@/components/index/Actions';
import {
    type BioCandidate,
    BioCandidateStatusEnum,
    BioLengthEnum,
    BioPerspectiveEnum,
    type BioRefinement,
    BioRefinementStatusEnum,
    BioToneEnum,
    type UserContent,
    userAtom,
    Brief,
} from '@/components/with_auth/State';
import LineBreakParagraphs from '@/components/commons/page-elements/LineBreakParagraph';
import { BIO_GEN_TEMPERATURE } from '@/utils/index';

import bioStyles from './Bio.module.css';
import ToolLabel from './ToolLabel';
import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import customModalTheme from './UploadFileModalTheme';

const refinementLengthOptions = [BioLengthEnum.Short, BioLengthEnum.Long];
function ReviseBioPanel({
    isModal = false,
    isEdit,
    openBrief,
    extendContinue = null,
    overrideBack = null,
}: bioPageProps) {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [breakingError, setBreakingError] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState<BioCandidate>(null);
    const [activeRefinement, setActiveRefinement] = useState<BioRefinement>(null);
    const [formError, setFormError] = useState('');

    const [user, setUser] = useRecoilState(userAtom);

    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);
    const [createBioRefinement] = useMutation(CREATE_BIO_REFINEMENT);
    const { loading, data } = useQuery(FETCH_BIO_BATCH, {
        fetchPolicy: 'no-cache',
    });
    const [brief, setCurrentBrief] = useState<Brief>(openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex) : user.briefs[0]);

    useEffect(() => {
        const currentBrief = openBrief
            ? user.briefs.find((item) => item.id === openBrief.briefIndex)
            : user.briefs[0];

        if (currentBrief) {
            setCurrentBrief(currentBrief);
        }
    }, [openBrief, user]);
    const [
        getBioRefinement,
        { loading: refinementLoading, data: refinementData, stopPolling },
    ] = useLazyQuery(FETCH_BIO_REFINEMENT, {
        // poll 5 seconds after we call getBioRefinement the first time
        pollInterval: 5 * 1000,
    });

    const handleBackNavigation = useCallback(() => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_SELECT_STARTER_BIO);
        }
    }, [overrideBack, router, isModal]);

    // check if we have any bioBatch candidates, if not
    // go back to the career history page
    useEffect(() => {
        if (loading) {
            return;
        }

        if (!data?.user || !brief?.bioBatch?.id) {
            return;
        }

        const { bioBatches, bioRefinements } = data.user;
        const singleBioBatch = bioBatches.filter(
            (bio) => bio.id === brief.bioBatch.id,
        )[0];

        if (!singleBioBatch || !singleBioBatch?.candidates.length) {
            handleBackNavigation();
            return;
        }

        const candidates = Array.from<BioCandidate>(singleBioBatch?.candidates);
        // find the selected candidate
        const selected: BioCandidate = candidates.find(
            (item) => item.status === BioCandidateStatusEnum.Selected,
        );
        if (selected) {
            setSelectedCandidate(selected);
        } else {
            // we couldn't find one, this is a breaking issue
            // send them back to select a candidate
            // they'll need to go back and select a candidate
            handleBackNavigation();
            return;
        }

        // Find the latest bio refinement for and see if it's pending
        // filter out refinements not part of the selected candidate
        const newRefinements = bioRefinements.filter(
            (item: BioRefinement) => item.bioCandidate.id === selected.id,
        );
        // sort them by createdAt
        newRefinements.sort(
            (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
        );

        // Pull the last one out of our temp array
        const refinement: BioRefinement = newRefinements.length ? newRefinements.pop() : null;
        setActiveRefinement(refinement);
        // start polling for status change if we have a refinement
        if (refinement) {
            getBioRefinement({
                variables: {
                    id: refinement.id,
                },
            });
        }
    }, [loading, data, getBioRefinement, handleBackNavigation, brief?.bioBatch?.id]);

    // check if our active refinement is still pending
    useEffect(() => {
        if (refinementLoading) {
            return;
        }

        // newRefinements should only return the id we searched for
        // in our getBioRefinement request
        const newRefinements = refinementData?.user?.bioRefinements;
        if (newRefinements?.length) {
            const newRefinement: BioRefinement = newRefinements[0];
            if (
                newRefinement.status === BioRefinementStatusEnum.Failed
                || newRefinement.status === BioRefinementStatusEnum.NoData
            ) {
                setBreakingError(true);
                stopPolling();
            } else if (newRefinement.status === BioRefinementStatusEnum.Succeeded) {
                setActiveRefinement(newRefinement);
                stopPolling();
            }
        }
    }, [refinementLoading, refinementData, stopPolling]);

    const handleContinue = async () => {
        try {
            const biography = activeRefinement
                && activeRefinement.status === BioRefinementStatusEnum.Succeeded
                ? activeRefinement.bio : selectedCandidate?.bio;

            // if we aren't in modal flow, set our onboardingState
            setIsLoading(true);
            if (!isModal) {
                // update our onboarding state
                const userAttrs: Partial<UserContent> = {
                    onboardingState: BIO_STATES.editBio,
                };
                const userRes = await updateUser({
                    variables: {
                        updateUserInput: {
                            attributes: userAttrs,
                        },
                    },
                });
                const newUser = userRes?.data?.updateUser?.user;
                if (newUser) {
                    setUser(newUser);
                }
            }
            // TODO(tom) Check if we save id of initial open brief so we can avoid this
            const briefId = openBrief ? openBrief.briefIndex : user.briefs[0].id;
            // update the brief's biography
            const response = await updateBrief({
                variables: {
                    input: {
                        id: briefId,
                        attributes: {
                            biography,
                        },
                    },
                },
            });

            setIsLoading(false);

            const newBrief = response?.data?.updateBrief?.brief;
            if (newBrief) {
                // update the brief on the user for recoil
                const newUser = { ...user };
                newUser.briefs = newUser.briefs.map((item) => {
                    if (item.id === briefId) {
                        return { ...item, biography };
                    }
                    if (formError !== '') setFormError('');
                    return item;
                });
                setUser(newUser);

                if (isModal && extendContinue) {
                    extendContinue('', isEdit);
                } else {
                    router.push(routes.BIO_EDIT_BIO);
                }
                if (formError !== '') setFormError('');
            } else {
                setFormError('An unexpected error occurred.');
            }
        } catch (err) {
            // Logs and sends to sentry
            reportGeneralError(err);
        }
    };

    const handleUpdate = async (form, { setSubmitting }) => {
        const length = refinementLengthOptions[form.length];

        let input = selectedCandidate?.bio;
        // if we have an active refinement that succeeded use it as the
        // input instead of the candidate
        if (activeRefinement && activeRefinement.status === BioRefinementStatusEnum.Succeeded) {
            input = activeRefinement.bio;
        }

        // only run the create bio refinement if we don't
        // have a pending bioRefinement already
        const attributes = {
            // Do not use for
            bioCandidateId: selectedCandidate?.id,
            perspective: form.perspective,
            tone: form.tone,
            temperature: BIO_GEN_TEMPERATURE,
            input,
            length,
        };
        const response = await createBioRefinement({
            variables: {
                input: {
                    attributes,
                },
            },
        });

        const newRefinement: BioRefinement = response?.data?.createBioRefinement?.bioRefinement;
        if (newRefinement) {
            setActiveRefinement(newRefinement);
            // poll for changes of refinement
            getBioRefinement({
                variables: {
                    id: newRefinement.id,
                },
            });
            if (formError !== '') setFormError('');
        } else if (response?.data?.createBioRefinement?.errors.length > 0) {
            setFormError('Please allow time for the pending refinement to complete.');
        } else {
            if (formError !== '') setFormError('');
            setBreakingError(true);
        }

        setSubmitting(false);
    };

    const renderModalContent = () => {
        let title = 'Refining biography';
        let body = 'Just a moment...';
        let button = 'Skip';

        // TODO(tom) We fixed a bug in the flow so users can't view
        // this modal/screen if they haven't selected a bio candidate
        // and will always be sent back to selection if they didn't choose
        if (!selectedCandidate) {
            title = 'No Selected Candidate';
            body = 'Please go back and select a candidate to continue refining it.';
            button = 'Back';
        } else if (breakingError) {
            title = 'Unexpected error';
            body = 'Please continue with manual bio entry';
            button = 'Continue';
        }
        return (
            <>
                <p className="text-[20px] font-bold">{title}</p>
                <p>{body}</p>
                {
                    !breakingError
                    && selectedCandidate
                    && (
                        <div className="flex items-center justify-center">
                            <Spinner size="xl" />
                        </div>
                    )
                }
                <Button
                    color="dark"
                    className="bg-cleanSlate"
                    onClick={!selectedCandidate ? handleBackNavigation : handleContinue}
                >
                    {button}
                </Button>
            </>
        );
    };

    const bio = activeRefinement
        && activeRefinement.status === BioRefinementStatusEnum.Succeeded
        ? activeRefinement.bio : selectedCandidate?.bio;

    // find the active refinement
    let pending = false;
    if (activeRefinement) {
        pending = activeRefinement.status === BioRefinementStatusEnum.Pending;
    }

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8 sm:pb-0" step={2} totalSteps={5} isModal={isModal}>
            <div className="w-full py-2 pb-0 sm:px-5">
                <BioPageHeader
                    header="Now, let&apos;s refine your draft."
                    subHeader="We all know it's much easier to edit than to write something from scratch. Refine the draft you've selected until it sounds most like you by adjusting the tone, length and perspective. You'll be able to edit the actual text in a moment."
                    subHeaderWidth="max-w-[650px]"
                    subHeaderPadding="pb-4"
                />
                {
                    formError !== '' && (
                        <div className="w-full my-[20px]">
                            <WarningCard message={formError} />
                        </div>
                    )
                }
                <div className="rounded-lg border border-gray-200 bg-cirrusWhite shadow-md flex flex-col">
                    <Formik
                        onSubmit={handleUpdate}
                        initialValues={{
                            tone: BioToneEnum.Professional,
                            perspective: BioPerspectiveEnum.FirstPerson,
                            length: '1',
                        }}
                    >
                        {({
                            values,
                            isSubmitting,
                            submitForm,
                            setFieldValue,
                        }) => (
                            <div className="flex items-center flex-wrap gap-4 p-2 sm:p-8 w-full justify-between">
                                {/* Start Tone */}
                                <ToolLabel labelText="Tone">
                                    <Select
                                        name="tone"
                                        className="w-full sm:w-[163px]"
                                        onChange={(e) => setFieldValue('tone', e.target.value)}
                                        value={values.tone}
                                    >
                                        <option
                                            value={BioToneEnum.Professional}
                                        >
                                            Professional
                                        </option>
                                        <option
                                            value={BioToneEnum.Casual}
                                        >
                                            Casual
                                        </option>
                                        <option
                                            value={BioToneEnum.Factual}
                                        >
                                            Factual
                                        </option>
                                    </Select>
                                </ToolLabel>
                                {/* Start Perspective */}
                                <ToolLabel labelText="Perspective">
                                    <Select
                                        className="w-full sm:w-[163px]"
                                        onChange={(e) => setFieldValue('perspective', e.target.value)}
                                        value={values.perspective}
                                        name="perspective"
                                    >
                                        <option
                                            value={BioPerspectiveEnum.FirstPerson}
                                        >
                                            1st Person
                                        </option>
                                        <option
                                            value={BioPerspectiveEnum.ThirdPerson}
                                        >
                                            3rd Person
                                        </option>

                                    </Select>
                                </ToolLabel>
                                <ToolLabel labelText="Length">
                                    <div className="w-full sm:w-auto">
                                        <RangeSlider
                                            onChange={(e) => setFieldValue('length', e.target.value)}
                                            value={values.length}
                                            name="length"
                                            className="w-full sm:w-[191px]"
                                            min={0}
                                            max={1}
                                            step={1}
                                        />
                                        <div className="flex justify-between text-center text-cleanSlate font-instrument text-xs font-normal leading-6">
                                            <span>Short</span>
                                            <span>Long</span>
                                        </div>
                                    </div>
                                </ToolLabel>

                                <div className="ml-auto">
                                    <Button
                                        onClick={submitForm}
                                        color="gray"
                                        disabled={isSubmitting || pending}
                                        isProcessing={isSubmitting || pending}
                                    >
                                        Update
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Formik>
                    <div className="border-t border-t-gray-200 resize-none min-h-[319px] text-cleanSlate font-instrument text-base font-normal leading-7 p-8">
                        <LineBreakParagraphs text={bio} externalStyles="text-sm" />
                    </div>
                </div>
            </div>
            <NavButtons
                containerStyles={bioStyles.buttonGroupWithoutBio}
                handleBackNavigation={handleBackNavigation}
                handleContinue={handleContinue}
                isLoading={isLoading}
            />

            <Modal
                className="absolute z-[1000]"
                theme={customModalTheme}
                show={pending || breakingError}
                position="center"
                size="sm"
            >
                {renderModalContent()}
            </Modal>
        </PanelContainer>
    );
}

export default ReviseBioPanel;
